import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { PrioTheme } from '../../../theme/types';
import { Menu } from '@prio365/prio365-react-library';
import MenuItem from '@prio365/prio365-react-library/lib/Menu/components/MenuItem';
import { NewDocumentType } from '../../../models/Document';
import { apiFetchDocumentPrefix } from '../api';
import { useTranslation } from 'react-i18next';
import { ProjectId } from '../../../models/Types';
import { PillCRA as Pill } from '@prio365/prio365-react-library';
import { createSelector } from 'reselect';
import { RootReducerState } from '../../../apps/main/rootReducer';
import { SettingState } from '../../settings/reducers/setting';
import { ESettingScope, Setting } from '../../../models/Settings';
import { useSelector } from 'react-redux';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    padding: 0,
  },
  menuItem: {
    margin: 0,
    fontSize: theme.font.fontSize.small,
    '& .prio-menu-suffixIcon': {
      display: 'flex',
    },
  },
  activeButton: {
    background: `${theme.colors.application.background.hover}!important`,
  },
  folderIcon: {
    '& > div > div > span > svg': {
      color: `${theme.colors.base.yellow.default}!important`,
    },
  },
  wordIcon: {
    '& > div > div > span > svg': {
      color: `${theme.colors.mimeType.word}!important`,
    },
  },
  powerPointIcon: {
    '& > div > div > span > svg': {
      color: theme.colors.mimeType.powerpoint,
    },
  },
  excelIcon: {
    '& > div > div > span > svg': {
      color: theme.colors.mimeType.excel,
    },
  },
}));

const betaSettingsSelector = createSelector<
  [(state: RootReducerState) => SettingState],
  Setting[]
>(
  (state) => state.settings.setting,
  ({ settings }) => {
    return settings.filter(
      (setting) =>
        setting.settingId !== '00000000-0000-0000-0000-000000000000' &&
        setting.key === 'General_BetaFeaturesEnabled'
    );
  }
);

interface CreateNewDriveItemDropdownOptionsProps {
  className?: string;
  projectId: ProjectId;
  setOpenNewDocDrawer?: (open: boolean) => void;
  setOpenNewDocFromTamplateDrawer?: (open: boolean) => void;
  setOpenLegacyDocFromTemplateDrawer?: (open: boolean) => void;
  setIsDropdownNewDocumentOpen?: (open: boolean) => void;
  onDocumentTypeSelect?: (id: NewDocumentType, prefix: string) => void;
  onNewFolderClick?: () => void;
  isRoot?: boolean;
  disableFolderMenuItem?: boolean;
}

export const CreateNewDriveItemDropdownOptions: React.FC<
  CreateNewDriveItemDropdownOptionsProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    className,
    projectId,
    setOpenNewDocFromTamplateDrawer,
    setOpenNewDocDrawer,
    setOpenLegacyDocFromTemplateDrawer,
    setIsDropdownNewDocumentOpen,
    onDocumentTypeSelect,
    onNewFolderClick,
    isRoot,
    disableFolderMenuItem,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const betaSettings = useSelector(betaSettingsSelector);

  const betaUserSettings = betaSettings.filter(
    (setting) => setting.scope === ESettingScope.USER
  );

  const isBetaFeaturesEnabled = useMemo(() => {
    const defaultSetting =
      betaUserSettings.find((setting) => setting.isDefault === true) ||
      betaUserSettings[0] ||
      betaSettings[0];

    const value = JSON.parse(defaultSetting?.jsonValue || '{}');
    return value?.isEnabled ?? false;
  }, [betaSettings, betaUserSettings]);

  const [prefix, setPrefix] = useState<string>(null);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const newDocument = () => {
    setOpenNewDocFromTamplateDrawer(true);
    if (setIsDropdownNewDocumentOpen) {
      setIsDropdownNewDocumentOpen(false);
    }
  };

  const newLegacyDocument = () => {
    setOpenLegacyDocFromTemplateDrawer(true);
    if (setIsDropdownNewDocumentOpen) {
      setIsDropdownNewDocumentOpen(false);
    }
  };

  const addNewEmptyDocument = async (id: NewDocumentType | 'template') => {
    if (setIsDropdownNewDocumentOpen) {
      setIsDropdownNewDocumentOpen(false);
    }
    if (id === 'template') {
      newDocument();
      return;
    } else {
      setOpenNewDocDrawer(true);
      onDocumentTypeSelect(id, prefix);
    }
  };

  const handleNewFolderClick = () => {
    if (setIsDropdownNewDocumentOpen) {
      setIsDropdownNewDocumentOpen(false);
    }
    if (onNewFolderClick) {
      onNewFolderClick();
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const fetchPrefix = async () => {
      const { data } = await apiFetchDocumentPrefix(projectId);
      if (data) {
        setPrefix(data.value);
      } else {
        setPrefix(null);
      }
    };
    fetchPrefix();
  }, [projectId]);
  //#endregion

  return (
    <Menu className={classNames(className, classes.root)} selectedItemId="">
      <MenuItem
        className={classNames(classes.menuItem, classes.folderIcon)}
        id="folder"
        icon={['fas', 'folder']}
        label={t('documents:newMenu.folder')}
        onClick={handleNewFolderClick}
        disabled={disableFolderMenuItem}
      />
      <MenuItem
        className={classNames(classes.menuItem, classes.wordIcon)}
        id="docx"
        icon={['fas', 'file-word']}
        label={t('documents:newMenu.word')}
        onClick={() => addNewEmptyDocument('docx')}
        disabled={isRoot}
      />
      <MenuItem
        className={classNames(classes.menuItem, classes.powerPointIcon)}
        id="pptx"
        icon={['fas', 'file-powerpoint']}
        label={t('documents:newMenu.powerpoint')}
        onClick={() => addNewEmptyDocument('pptx')}
        disabled={isRoot}
      />
      <MenuItem
        className={classNames(classes.menuItem, classes.excelIcon)}
        id="xlsx"
        icon={['fas', 'file-excel']}
        label={t('documents:newMenu.excel')}
        onClick={() => addNewEmptyDocument('xlsx')}
        disabled={isRoot}
      />
      <MenuItem
        className={classNames(classes.menuItem)}
        id="template-legacy"
        label={t('documents:newMenu.template')}
        suffixIcon={['fal', 'arrow-right']}
        onClick={() => newLegacyDocument()}
        disabled={isRoot}
      />
      {isBetaFeaturesEnabled && (
        <MenuItem
          className={classNames(classes.menuItem)}
          id="template-beta"
          label={
            (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div>{t('documents:newMenu.template')}</div>
                <Pill size="small" children={t('common:beta')} type="primary" />
              </div>
            ) as any
          }
          suffixIcon={['fal', 'arrow-right']}
          onClick={() => addNewEmptyDocument('template')}
          disabled={isRoot}
        />
      )}
    </Menu>
  );
};

export default CreateNewDriveItemDropdownOptions;
